import { forwardRef, type InputHTMLAttributes } from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface CheckboxProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** Visible label text. If omitted, the checkbox renders alone. */
  label?: string;
  /** Optional helper text shown below the label. */
  description?: string;
}

/**
 * Accessible custom checkbox. Native input stays focusable + clickable;
 * a styled square sits visually on top.
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ className, label, description, id, checked, ...props }, ref) => {
    const inputId = id ?? `cb-${Math.random().toString(36).slice(2, 9)}`;

    return (
      <label
        htmlFor={inputId}
        className={cn(
          'group inline-flex items-start gap-2.5 cursor-pointer',
          props.disabled && 'cursor-not-allowed opacity-60',
          className,
        )}
      >
        <span className="relative inline-flex items-center justify-center mt-0.5">
          <input
            id={inputId}
            ref={ref}
            type="checkbox"
            checked={checked}
            className="peer sr-only"
            {...props}
          />
          <span
            aria-hidden="true"
            className={cn(
              'h-4 w-4 rounded border border-ink-300 bg-white transition-colors',
              'peer-focus-visible:ring-2 peer-focus-visible:ring-lavender-400 peer-focus-visible:ring-offset-2',
              'peer-checked:bg-lavender-300 peer-checked:border-lavender-500',
              'group-hover:border-lavender-400',
            )}
          />
          {checked && (
            <Check
              aria-hidden="true"
              className="absolute h-3 w-3 text-lavender-900 pointer-events-none"
              strokeWidth={3}
            />
          )}
        </span>
        {(label || description) && (
          <span className="select-none">
            {label && (
              <span className="block text-sm text-ink-800 leading-tight">{label}</span>
            )}
            {description && (
              <span className="block text-xs text-ink-500 mt-0.5">{description}</span>
            )}
          </span>
        )}
      </label>
    );
  },
);
Checkbox.displayName = 'Checkbox';
